import { BarcodeScanner } from "@capacitor-mlkit/barcode-scanning";
import type { PluginListenerHandle } from "@capacitor/core";

// While this class is on <body>, variables.css hides the IonApp (scanner-hide)
// and shows the BarcodeScannerHeader close button, so the camera that ML Kit
// renders behind the WebView is visible.
const SCANNER_BODY_CLASS = "qrscanner";

let activeListener: PluginListenerHandle | null = null;
let activeTimeout: ReturnType<typeof setTimeout> | null = null;
let resolveActiveScan: ((value: string | null) => void) | null = null;

/**
 * Checks camera permission, prompting the user when it has not been decided yet.
 * Returns true when the camera can be used.
 */
export const ensureBarcodeCameraPermission = async (): Promise<boolean> => {
  let { camera } = await BarcodeScanner.checkPermissions();
  if (camera === "prompt" || camera === "prompt-with-rationale") {
    ({ camera } = await BarcodeScanner.requestPermissions());
  }
  return camera === "granted" || camera === "limited";
};

/**
 * Stops any running scan, restores the app UI, and resolves a pending
 * scanBarcodeOnce() with null. Safe to call when no scan is running.
 */
export const stopBarcodeScan = async (): Promise<void> => {
  await finishScan(null);
};

/**
 * Opens the camera scanner and resolves with the first barcode's raw value,
 * or null if the scan was cancelled (stopBarcodeScan) or timed out.
 * Callers are expected to have checked permission first.
 */
export const scanBarcodeOnce = async (timeoutMs?: number): Promise<string | null> => {
  // Only one scan can own the camera; cancel any scan still in progress.
  await finishScan(null);

  let settled = false;
  const result = new Promise<string | null>((resolve) => {
    resolveActiveScan = (value) => {
      settled = true;
      resolve(value);
    };
  });

  try {
    document.body.classList.add(SCANNER_BODY_CLASS);
    activeListener = await BarcodeScanner.addListener("barcodesScanned", (event) => {
      const barcode = event.barcodes[0];
      finishScan(barcode?.rawValue ?? barcode?.displayValue ?? null);
    });
    if (timeoutMs) {
      activeTimeout = setTimeout(() => finishScan(null), timeoutMs);
    }
    await BarcodeScanner.startScan();
  } catch (error) {
    resolveActiveScan = null;
    await cleanUp();
    // A scan already finished (e.g. cancelled while starting) keeps its result.
    if (!settled) throw error;
  }
  return result;
};

const finishScan = async (value: string | null) => {
  const resolve = resolveActiveScan;
  resolveActiveScan = null;
  await cleanUp();
  resolve?.(value);
};

const cleanUp = async () => {
  if (activeTimeout) {
    clearTimeout(activeTimeout);
    activeTimeout = null;
  }
  document.body.classList.remove(SCANNER_BODY_CLASS);
  const listener = activeListener;
  activeListener = null;
  try {
    await listener?.remove();
    await BarcodeScanner.stopScan();
  } catch (error) {
    console.error("Error stopping barcode scan:", error);
  }
};
